/**
 * Los campos que definen una regla: a qué productos y cuánto.
 *
 * Viven acá y no en `NuevaActualizacion` porque una programación es exactamente la misma
 * regla con una fecha pegada. Si estuvieran duplicados, el día que se agregue un tipo de
 * ajuste nuevo va a aparecer en una pantalla y no en la otra.
 *
 * Son controlados y sin estado propio: reciben el valor y avisan el cambio. El único
 * estado local es el catálogo (categorías y marcas), que se pide una sola vez y sólo
 * cuando el comerciante elige filtrar.
 */

import {
  Box,
  Card,
  Checkbox,
  Divider,
  Input,
  Label,
  Radio,
  Select,
  Text,
} from '@nimbus-ds/components';
import { useEffect, useState, type ReactElement } from 'react';
import {
  api,
  type Ajuste,
  type Alcance,
  type CampoAfectado,
  type Categoria,
  type Direccion,
  type Marca,
  type Redondeo,
} from '../api';
import { Cargando } from './Estado';
import { EntradaDePesos } from './EntradaDePesos';
import { numero } from '../formato';

export const ALCANCE_INICIAL: Alcance = {
  allProducts: true,
  categoryIds: [],
  includeSubcategories: true,
  brands: [],
  priceMin: null,
  priceMax: null,
  onlyPublished: false,
  onlyVisible: false,
  exclude: { categoryIds: [], brands: [], productIds: [], variantIds: [] },
};

/**
 * "Sólo algunos" sin ningún criterio elegido.
 *
 * No es lo mismo que "todo el catálogo": una regla así no dice nada y el backend la
 * rechazaría. Se bloquea antes de gastarle al comerciante un barrido entero.
 */
export function sinCriterio(alcance: Alcance): boolean {
  return (
    !alcance.allProducts &&
    alcance.categoryIds.length === 0 &&
    alcance.brands.length === 0 &&
    alcance.priceMin === null &&
    alcance.priceMax === null
  );
}

export function ajustePorDefecto(kind: string): Ajuste {
  switch (kind) {
    case 'fixed_amount':
      return { kind: 'fixed_amount', amount: 0 };
    case 'set_price':
      return { kind: 'set_price', amount: 100 };
    case 'target_margin':
      return { kind: 'target_margin', percent: 50, basis: 'markup' };
    default:
      return { kind: 'percentage', percent: 12 };
  }
}

export function redondeoPorDefecto(kind: string): Redondeo {
  switch (kind) {
    case 'ending':
      return { kind: 'ending', ending: '900', direction: 'nearest' };
    case 'multiple':
      return { kind: 'multiple', multiple: 100, direction: 'nearest' };
    default:
      return { kind: 'none' };
  }
}

// --- Qué productos -----------------------------------------------------------

interface CamposDeAlcanceProps {
  readonly alcance: Alcance;
  readonly onAlcance: (valor: Alcance) => void;
}

export function CamposDeAlcance({ alcance, onAlcance }: CamposDeAlcanceProps): ReactElement {
  const [categorias, setCategorias] = useState<Categoria[] | null>(null);
  const [marcas, setMarcas] = useState<Marca[] | null>(null);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);

  useEffect(() => {
    if (alcance.allProducts) return;
    if (categorias !== null && marcas !== null) return;

    let vigente = true;
    setCargandoCatalogo(true);
    void Promise.all([api.categorias(), api.marcas()])
      .then(([c, m]) => {
        if (!vigente) return;
        setCategorias(c.categorias);
        setMarcas(m.marcas);
      })
      .catch(() => undefined)
      .finally(() => {
        if (vigente) setCargandoCatalogo(false);
      });
    return () => {
      vigente = false;
    };
  }, [alcance.allProducts, categorias, marcas]);

  const marcasConVariasGrafias = (marcas ?? []).filter((marca) => marca.grafias.length > 1);

  return (
    <Card>
      <Card.Header title="Qué productos" />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Box display="flex" flexDirection="column" gap="2">
            <Text fontWeight="medium">Elegí una</Text>
            <Radio
              id="todo"
              name="alcance"
              label="Todo el catálogo"
              checked={alcance.allProducts}
              onChange={() => onAlcance({ ...alcance, allProducts: true })}
            />
            <Radio
              id="filtrado"
              name="alcance"
              label="Sólo algunos"
              checked={!alcance.allProducts}
              onChange={() => onAlcance({ ...alcance, allProducts: false })}
            />
          </Box>

          {!alcance.allProducts && cargandoCatalogo && (
            <Cargando texto="Buscando tus categorías y marcas…" />
          )}

          {!alcance.allProducts && !cargandoCatalogo && (
            <Box display="flex" flexDirection="column" gap="4" paddingLeft={{ xs: '2', md: '4' }}>
              <Box display="flex" flexDirection="column" gap="2">
                <Label htmlFor="categorias">Categorías</Label>
                <Select
                  id="categorias"
                  name="categorias"
                  multiple
                  value={alcance.categoryIds.map(String)}
                  onChange={(evento) =>
                    onAlcance({
                      ...alcance,
                      categoryIds: [...evento.currentTarget.selectedOptions].map((opcion) =>
                        Number(opcion.value),
                      ),
                    })
                  }
                >
                  {(categorias ?? []).map((categoria) => (
                    <Select.Option
                      key={categoria.id}
                      value={String(categoria.id)}
                      label={categoria.ruta === '' ? categoria.nombre : categoria.ruta}
                    />
                  ))}
                </Select>
                <Checkbox
                  id="subcategorias"
                  name="subcategorias"
                  label="Incluir las subcategorías"
                  checked={alcance.includeSubcategories}
                  onChange={(evento) =>
                    onAlcance({ ...alcance, includeSubcategories: evento.currentTarget.checked })
                  }
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="2">
                <Label htmlFor="marcas">Marcas</Label>
                <Select
                  id="marcas"
                  name="marcas"
                  multiple
                  value={alcance.brands}
                  onChange={(evento) =>
                    onAlcance({
                      ...alcance,
                      brands: [...evento.currentTarget.selectedOptions].map(
                        (opcion) => opcion.value,
                      ),
                    })
                  }
                >
                  {(marcas ?? []).map((marca) => (
                    <Select.Option
                      key={marca.clave}
                      value={marca.clave}
                      label={`${marca.nombre} (${numero(marca.productos)})`}
                    />
                  ))}
                </Select>
                {marcasConVariasGrafias.length > 0 && (
                  <Text fontSize="caption" color="neutral-textLow">
                    Detectamos la misma marca escrita de varias formas (
                    {marcasConVariasGrafias.map((marca) => marca.grafias.join(', ')).join(' · ')}).
                    Las tratamos como una sola.
                  </Text>
                )}
              </Box>

              {/*
                Los dos campos de precio comparten renglón en pantalla ancha. En 375px no
                entran: uno de los dos quedaría de 60px y el comerciante escribiría a
                ciegas. Apilados ocupan más alto y se leen enteros.
              */}
              <Box
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={{ xs: '3', md: '2' }}
                flexWrap="wrap"
              >
                <Box display="flex" flexDirection="column" gap="1">
                  <EntradaDePesos
                    id="precio-min"
                    etiqueta="Precio desde"
                    placeholder="0"
                    valor={alcance.priceMin}
                    onValor={(centavos) => onAlcance({ ...alcance, priceMin: centavos })}
                  />
                </Box>
                <Box display="flex" flexDirection="column" gap="1">
                  <EntradaDePesos
                    id="precio-max"
                    etiqueta="Precio hasta"
                    placeholder="Sin límite"
                    valor={alcance.priceMax}
                    onValor={(centavos) => onAlcance({ ...alcance, priceMax: centavos })}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/*
            *** Dos tipos de control que no son lo mismo. ***

            Arriba, "Todo el catálogo" y "Sólo algunos" son **excluyentes**: uno o el otro.
            Abajo, estos dos son **filtros que se suman** a lo que se haya elegido arriba.

            Al mismo nivel visual parecían cuatro opciones del mismo tipo, y entonces
            "Sólo productos publicados" se lee como una tercera alternativa a "Todo el
            catálogo". El divisor y el título dicen de qué se trata cada mitad.
          */}
          <Divider />

          <Box display="flex" flexDirection="column" gap="2">
            <Text fontWeight="medium">Y además, dejar afuera</Text>
            <Text fontSize="caption" color="neutral-textLow">
              Se aplican sobre lo que elegiste arriba. Sin tildar nada, entra todo.
            </Text>
            <Checkbox
              id="publicados"
              name="publicados"
              label="Sólo productos publicados"
              checked={alcance.onlyPublished}
              onChange={(evento) =>
                onAlcance({ ...alcance, onlyPublished: evento.currentTarget.checked })
              }
            />
            <Checkbox
              id="visibles"
              name="visibles"
              label="Sólo productos visibles en la tienda"
              checked={alcance.onlyVisible}
              onChange={(evento) =>
                onAlcance({ ...alcance, onlyVisible: evento.currentTarget.checked })
              }
            />
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

// --- Cuánto cambia -----------------------------------------------------------

interface CamposDeAjusteProps {
  readonly ajuste: Ajuste;
  readonly redondeo: Redondeo;
  readonly campos: CampoAfectado;
  readonly onAjuste: (valor: Ajuste) => void;
  readonly onRedondeo: (valor: Redondeo) => void;
  readonly onCampos: (valor: CampoAfectado) => void;
}

export function CamposDeAjuste(props: CamposDeAjusteProps): ReactElement {
  const { ajuste, redondeo, campos } = props;

  return (
    <>
      <Card>
        <Card.Header title="Cuánto cambia" />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="4">
            <Select
              id="tipo-ajuste"
              name="tipo-ajuste"
              value={ajuste.kind}
              onChange={(evento) => props.onAjuste(ajustePorDefecto(evento.currentTarget.value))}
            >
              <Select.Option value="percentage" label="Un porcentaje" />
              <Select.Option value="fixed_amount" label="Un monto fijo" />
              <Select.Option value="set_price" label="Fijar un precio puntual" />
              <Select.Option value="target_margin" label="Llegar a un margen sobre el costo" />
            </Select>

            {ajuste.kind === 'percentage' && (
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="porcentaje">Porcentaje (negativo para bajar)</Label>
                <Input
                  id="porcentaje"
                  name="porcentaje"
                  type="number"
                  value={String(ajuste.percent)}
                  onChange={(evento) =>
                    props.onAjuste({ kind: 'percentage', percent: Number(evento.currentTarget.value) })
                  }
                />
              </Box>
            )}

            {(ajuste.kind === 'fixed_amount' || ajuste.kind === 'set_price') && (
              <Box display="flex" flexDirection="column" gap="1">
                <EntradaDePesos
                  id="monto"
                  etiqueta={ajuste.kind === 'set_price' ? 'Precio' : 'Monto (negativo para bajar)'}
                  placeholder="0,00"
                  valor={ajuste.amount}
                  onValor={(centavos) =>
                    props.onAjuste({ kind: ajuste.kind, amount: centavos ?? 0 } as Ajuste)
                  }
                />
              </Box>
            )}

            {ajuste.kind === 'target_margin' && (
              <Box display="flex" flexDirection="column" gap="2">
                <Box display="flex" flexDirection="column" gap="1">
                  <Label htmlFor="margen">Margen (%)</Label>
                  <Input
                    id="margen"
                    name="margen"
                    type="number"
                    value={String(ajuste.percent)}
                    onChange={(evento) =>
                      props.onAjuste({ ...ajuste, percent: Number(evento.currentTarget.value) })
                    }
                  />
                </Box>
                <Select
                  id="base-margen"
                  name="base-margen"
                  value={ajuste.basis}
                  onChange={(evento) =>
                    props.onAjuste({
                      ...ajuste,
                      basis: evento.currentTarget.value as 'markup' | 'margin',
                    })
                  }
                >
                  <Select.Option value="markup" label="Recargo sobre el costo (costo 100 + 50% = 150)" />
                  <Select.Option value="margin" label="Margen sobre la venta (costo 100 + 50% = 200)" />
                </Select>
                <Text fontSize="caption" color="neutral-textLow">
                  Las variantes que no tengan costo cargado quedan afuera, y te lo avisamos en la
                  vista previa.
                </Text>
              </Box>
            )}
          </Box>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header title="Redondeo" />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="4">
            <Select
              id="tipo-redondeo"
              name="tipo-redondeo"
              value={redondeo.kind}
              onChange={(evento) => props.onRedondeo(redondeoPorDefecto(evento.currentTarget.value))}
            >
              <Select.Option value="none" label="Sin redondeo" />
              <Select.Option value="ending" label="A una terminación" />
              <Select.Option value="multiple" label="A un múltiplo" />
            </Select>

            {redondeo.kind === 'ending' && (
              <Select
                id="terminacion"
                name="terminacion"
                value={redondeo.ending}
                onChange={(evento) =>
                  props.onRedondeo({
                    ...redondeo,
                    ending: evento.currentTarget.value as '900' | '990' | '99' | '00',
                  })
                }
              >
                <Select.Option value="900" label="Terminación 900 — $12.900" />
                <Select.Option value="990" label="Terminación 990 — $12.990" />
                <Select.Option value="99" label="Terminación ,99 — $1.234,99" />
                <Select.Option value="00" label="Peso redondo — $1.234,00" />
              </Select>
            )}

            {redondeo.kind === 'multiple' && (
              <Select
                id="multiplo"
                name="multiplo"
                value={String(redondeo.multiple)}
                onChange={(evento) =>
                  props.onRedondeo({
                    ...redondeo,
                    multiple: Number(evento.currentTarget.value) as 100 | 500 | 1000,
                  })
                }
              >
                <Select.Option value="100" label="Múltiplo de $100" />
                <Select.Option value="500" label="Múltiplo de $500" />
                <Select.Option value="1000" label="Múltiplo de $1.000" />
              </Select>
            )}

            {redondeo.kind !== 'none' && (
              <Select
                id="direccion"
                name="direccion"
                value={redondeo.direction}
                onChange={(evento) =>
                  props.onRedondeo({
                    ...redondeo,
                    direction: evento.currentTarget.value as Direccion,
                  })
                }
              >
                <Select.Option value="nearest" label="Al más cercano" />
                <Select.Option value="up" label="Siempre para arriba" />
                <Select.Option value="down" label="Siempre para abajo" />
              </Select>
            )}
          </Box>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header title="Qué precio tocar" />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap="2">
            <Radio
              id="campo-precio"
              name="campos"
              label="Sólo el precio normal"
              checked={campos === 'price'}
              onChange={() => props.onCampos('price')}
            />
            <Radio
              id="campo-oferta"
              name="campos"
              label="Sólo el precio promocional"
              checked={campos === 'promotional_price'}
              onChange={() => props.onCampos('promotional_price')}
            />
            <Radio
              id="campo-ambos"
              name="campos"
              label="Los dos, manteniendo el mismo porcentaje de descuento"
              checked={campos === 'both'}
              onChange={() => props.onCampos('both')}
            />
          </Box>
        </Card.Body>
      </Card>
    </>
  );
}
